"use client";

import { ArrowDownIcon, ArrowUpIcon, MinusIcon } from "lucide-react";
import { memo } from "react";
import { cn } from "@/lib/utils";
import type { TransactionDirection } from "./types";

/**
 * Format a large number with appropriate suffixes (K, M, B, T)
 */
export function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000_000) {
    return `${(num / 1_000_000_000_000).toFixed(2)}T`;
  }
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toFixed(2);
}

/**
 * Format a token value with appropriate decimal places
 */
export function formatTokenValue(
  value: string | number,
  decimals = 18,
  maxDisplayDecimals = 6
): string {
  const numValue =
    typeof value === "string"
      ? Number.parseFloat(value) / 10 ** decimals
      : value;

  if (numValue === 0) {
    return "0";
  }

  // For very small values
  if (Math.abs(numValue) < 0.000_001) {
    return numValue.toExponential(2);
  }

  // For large values, use suffix format
  if (Math.abs(numValue) >= 1000) {
    return formatLargeNumber(numValue);
  }

  // Normal range - show appropriate decimal places
  const formatted = numValue.toFixed(maxDisplayDecimals);
  // Remove trailing zeros
  return formatted.replace(/\.?0+$/, "");
}

/**
 * Format USD value
 */
export function formatUsdValue(value: string | number): string {
  const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numValue)) {
    return "-";
  }

  if (numValue >= 1_000_000) {
    return `$${formatLargeNumber(numValue)}`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
}

export type ValueDisplayProps = {
  /** Token value (raw or formatted) */
  value: string | number;
  /** Token symbol to display */
  symbol?: string;
  /** Decimals for raw value conversion */
  decimals?: number;
  /** Optional USD value */
  usdValue?: string | number;
  /** Direction of the transfer */
  direction?: TransactionDirection;
  /** Show direction indicator */
  showDirection?: boolean;
  /** Show plus/minus sign */
  showSign?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
};

const PureValueDisplay = ({
  value,
  symbol,
  decimals = 18,
  usdValue,
  direction,
  showDirection = false,
  showSign = false,
  size = "md",
  className,
}: ValueDisplayProps) => {
  const formattedValue =
    typeof value === "string" && value.includes(".")
      ? value // Already formatted
      : formatTokenValue(value, decimals);

  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const iconSizes = {
    sm: "size-3",
    md: "size-3.5",
    lg: "size-4",
  };

  const directionConfig = {
    incoming: {
      icon: ArrowDownIcon,
      color: "text-accent",
      sign: "+",
    },
    outgoing: {
      icon: ArrowUpIcon,
      color: "text-destructive",
      sign: "-",
    },
    self: {
      icon: MinusIcon,
      color: "text-muted-foreground",
      sign: "",
    },
  };

  const config = direction ? directionConfig[direction] : null;
  const DirectionIcon = config?.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        sizeClasses[size],
        config?.color,
        className
      )}
    >
      {showDirection && DirectionIcon && (
        <DirectionIcon className={iconSizes[size]} />
      )}
      <span className="font-medium tabular-nums">
        {showSign && config?.sign}
        {formattedValue}
      </span>
      {symbol && <span className="text-muted-foreground">{symbol}</span>}
      {usdValue !== undefined && (
        <span className="text-muted-foreground">
          ({formatUsdValue(usdValue)})
        </span>
      )}
    </span>
  );
};

export const ValueDisplay = memo(PureValueDisplay);

/**
 * Display gas information
 */
export type GasDisplayProps = {
  gasUsed: string | number;
  gasPrice?: string | number;
  /** Native currency symbol (ETH, MATIC, etc.) */
  nativeSymbol?: string;
  /** Gas price unit (gwei) */
  showGwei?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
};

export const GasDisplay = memo(function GasDisplay({
  gasUsed,
  gasPrice,
  nativeSymbol = "ETH",
  showGwei = true,
  size = "sm",
  className,
}: GasDisplayProps) {
  const gasUsedNum =
    typeof gasUsed === "string" ? Number.parseInt(gasUsed, 10) : gasUsed;
  const gasPriceNum =
    gasPrice !== undefined
      ? typeof gasPrice === "string"
        ? Number.parseInt(gasPrice, 10)
        : gasPrice
      : 0;

  // Calculate total gas cost in native token
  const gasCostWei = BigInt(gasUsedNum) * BigInt(gasPriceNum);
  const gasCostEth = Number(gasCostWei) / 1e18;

  // Convert gas price to gwei
  const gasPriceGwei = gasPriceNum / 1e9;

  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-muted-foreground",
        sizeClasses[size],
        className
      )}
    >
      <span className="tabular-nums">{gasUsedNum.toLocaleString()}</span>
      <span>gas</span>
      {showGwei && gasPriceGwei > 0 && (
        <>
          <span className="text-muted-foreground/50">•</span>
          <span className="tabular-nums">{gasPriceGwei.toFixed(1)}</span>
          <span>gwei</span>
        </>
      )}
      {gasCostEth > 0 && (
        <>
          <span className="text-muted-foreground/50">•</span>
          <span className="tabular-nums">{gasCostEth.toFixed(6)}</span>
          <span>{nativeSymbol}</span>
        </>
      )}
    </span>
  );
});

/**
 * Display a method/function name badge
 */
export type MethodBadgeProps = {
  method: string;
  size?: "sm" | "md" | "lg";
  className?: string;
};

export const MethodBadge = memo(function MethodBadge({
  method,
  size = "md",
  className,
}: MethodBadgeProps) {
  const sizeClasses = {
    sm: "px-1.5 py-0.5 text-[10px]",
    md: "px-2 py-0.5 text-xs",
    lg: "px-2.5 py-1 text-sm",
  };

  // Clean up method name for display
  const displayMethod = method
    .replace(/\(.*\)/, "") // Remove parameters
    .replace(/^0x[a-f0-9]{8}$/i, "Unknown"); // Show "Unknown" for unrecognized selectors

  return (
    <span
      className={cn(
        "inline-flex items-center rounded border border-border bg-muted/50 font-mono",
        sizeClasses[size],
        className
      )}
    >
      {displayMethod}
    </span>
  );
});

/**
 * Display relative time
 */
export type TimeAgoProps = {
  timestamp: string | Date;
  size?: "sm" | "md" | "lg";
  className?: string;
};

export const TimeAgo = memo(function TimeAgo({
  timestamp,
  size = "md",
  className,
}: TimeAgoProps) {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let display: string;
  if (diffSecs < 60) {
    display = "just now";
  } else if (diffMins < 60) {
    display = `${diffMins}m ago`;
  } else if (diffHours < 24) {
    display = `${diffHours}h ago`;
  } else if (diffDays < 7) {
    display = `${diffDays}d ago`;
  } else {
    display = date.toLocaleDateString();
  }

  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  return (
    <span
      className={cn(
        "text-muted-foreground tabular-nums",
        sizeClasses[size],
        className
      )}
      title={date.toLocaleString()}
    >
      {display}
    </span>
  );
});
